import { CreateIndividualSchema, RelationSchema } from '@backend/schemas'
import { zodResolver } from '@hookform/resolvers/zod'
import i18n from 'i18next'
import { PencilIcon } from 'lucide-react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import z from 'zod'

import { useMe } from '@/api/hooks/useMe'
import { useUpdateIndividual } from '@/api/hooks/useUpdateIndividual'
import { useUpdateRelation } from '@/api/hooks/useUpdateRelation'
import { RELATION_OTHER_VALUE, RelationSelect } from '@/components/molecules/relation-select'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { IndividualType } from '@/hooks/useIndividuals'

const schema = z
  .object({
    firstName: z.string().min(1).max(50),
    gender: CreateIndividualSchema.shape.gender,
    lastName: z.string().min(1).max(50),
    relation: RelationSchema.optional(),
    relationOther: z.string().optional(),
  })
  .refine(data => data.relation !== RELATION_OTHER_VALUE || !!data.relationOther?.trim(), {
    error: () => i18n.t('formValidation.relationOtherRequired'),
    path: ['relationOther'],
  })

type EditIndividualInput = z.input<typeof schema>

type Props = {
  individual: IndividualType
  onOpenChange(open: boolean): void
  open: boolean
}

export const EditIndividualDialog = ({ individual, onOpenChange, open }: Props) => {
  const { t } = useTranslation()
  const { data: me } = useMe()
  const { mutateAsync: updateIndividual } = useUpdateIndividual()
  const { mutateAsync: updateRelation } = useUpdateRelation()

  // The current user's own relation to this individual (editable here).
  const ownEntry = individual.users.find(u => u.id === me?.id)
  const ownRelation = ownEntry?.relation
  const ownRelationOther = ownEntry?.relationOther

  const form = useForm<EditIndividualInput>({
    defaultValues: {
      firstName: individual.firstName,
      gender: individual.gender,
      lastName: individual.lastName,
      relation: ownRelation,
      relationOther: ownRelationOther,
    },
    resolver: zodResolver(schema),
  })

  // Reset to the latest values whenever the dialog (re)opens.
  useEffect(() => {
    if (open) {
      form.reset({
        firstName: individual.firstName,
        gender: individual.gender,
        lastName: individual.lastName,
        relation: ownRelation,
        relationOther: ownRelationOther,
      })
    }
  }, [open, individual, ownRelation, ownRelationOther, form])

  const gender = form.watch('gender')
  const relation = form.watch('relation')
  const relationOther = form.watch('relationOther')

  const handleSubmit = async (values: EditIndividualInput) => {
    try {
      await updateIndividual({
        individualId: individual.id,
        requestBody: {
          firstName: values.firstName,
          gender: values.gender,
          lastName: values.lastName,
        },
      })

      // Relation lives on the individual's users[] and has its own endpoint.
      if (
        values.relation &&
        (values.relation !== ownRelation || values.relationOther !== ownRelationOther)
      ) {
        await updateRelation({
          individualId: individual.id,
          requestBody: {
            relation: values.relation,
            relationOther:
              values.relation === RELATION_OTHER_VALUE ? values.relationOther : undefined,
          },
        })
      }

      onOpenChange(false)
    } catch (error) {
      toast.error((error as Error).message)
    }
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <PencilIcon />
          {t('homePage.header.editButton')}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('homePage.editDialog.title')}</DialogTitle>
          <DialogDescription>{t('homePage.editDialog.description')}</DialogDescription>
        </DialogHeader>

        <form className="space-y-4" onSubmit={form.handleSubmit(handleSubmit)}>
          <FieldGroup>
            <FieldGroup className="flex flex-row gap-4">
              <Field>
                <FieldLabel htmlFor="edit-firstName">
                  {t('defaultFormInputs.firstName.label')}
                </FieldLabel>
                <Input autoComplete="off" id="edit-firstName" {...form.register('firstName')} />
                <FieldError>{form.formState.errors.firstName?.message}</FieldError>
              </Field>
              <Field>
                <FieldLabel htmlFor="edit-lastName">
                  {t('defaultFormInputs.lastName.label')}
                </FieldLabel>
                <Input autoComplete="off" id="edit-lastName" {...form.register('lastName')} />
                <FieldError>{form.formState.errors.lastName?.message}</FieldError>
              </Field>
            </FieldGroup>

            <Field>
              <FieldLabel>
                {t('defaultFormInputs.gender.label')} {'('}
                {t('global.optional')}
                {')'}
              </FieldLabel>
              <Select
                onValueChange={value => form.setValue('gender', value as 'male')}
                value={gender ?? undefined}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder={t('defaultFormInputs.gender.placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="male">{t('defaultFormOptions.gender_male')}</SelectItem>
                    <SelectItem value="female">{t('defaultFormOptions.gender_female')}</SelectItem>
                    <SelectItem value="other">{t('defaultFormOptions.gender_other')}</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
              <FieldError>{form.formState.errors.gender?.message}</FieldError>
            </Field>

            {ownRelation && (
              <Field>
                <FieldLabel>{t('homePage.editDialog.relationLabel')}</FieldLabel>
                <RelationSelect
                  className="w-full"
                  includeOther
                  onValueChange={value =>
                    form.setValue('relation', value as z.infer<typeof RelationSchema>)
                  }
                  perspective="first-person"
                  value={relation}
                />
                <FieldError>{form.formState.errors.relation?.message}</FieldError>
              </Field>
            )}

            {relation === RELATION_OTHER_VALUE && (
              <Field>
                <FieldLabel htmlFor="edit-relationOther">
                  {t('relation.otherInputLabel')}
                </FieldLabel>
                <Input
                  autoComplete="off"
                  id="edit-relationOther"
                  onChange={event => form.setValue('relationOther', event.target.value)}
                  placeholder={t('relation.otherInputPlaceholder')}
                  value={relationOther ?? ''}
                />
                <FieldError>{form.formState.errors.relationOther?.message}</FieldError>
              </Field>
            )}
          </FieldGroup>

          <div className="flex justify-end gap-2">
            <Button onClick={() => onOpenChange(false)} type="button" variant="outline">
              {t('global.cancel')}
            </Button>
            <Button isLoading={form.formState.isSubmitting} type="submit">
              {t('homePage.editDialog.submitButton')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default EditIndividualDialog
